/**
 * Role features: editor controls that a superadmin can turn on or off per role
 * in Admin → Feature Flags, with no deploy. The server and the client both
 * read this table (as they read permissions.ts).
 *
 * A feature only shows or hides a control. It never gives access: an API that
 * the control calls keeps its own capability guard (permissions.ts). So a
 * feature whose control calls a guarded API names that capability in
 * `requires`, and the feature cannot be turned on for a role without it.
 *
 * Rules for a new feature (also in .claude/skills/role-features/SKILL.md):
 * - `defaultRoles` is the behaviour before the feature existed. Adding a
 *   feature must change nothing for any role until an admin changes a target.
 * - The key never changes: the database stores targets under it.
 * - The code reads a feature only with useFeature(key) (hooks/useFeature.ts).
 */

import {ALL_ROLES, can, type Capability, type UserRole} from "./permissions.ts";

export interface FeatureDef {
  /** Stable id, the `_id` of the feature_flags document. Matches ^[a-z0-9._-]+$. */
  readonly key: string;
  readonly name: string;
  /** What a role gets or loses. Shown in the admin. */
  readonly description: string;
  /** The admin groups features by this. */
  readonly area: FeatureArea;
  /** The roles that have the feature while no admin target is set. */
  readonly defaultRoles: readonly UserRole[];
  /** The capability that the control's API needs. Only roles with it can have the feature. */
  readonly requires?: Capability;
}

export const FEATURE_AREAS = [
  "Banner header",
  "Canvas shortcuts",
  "Backgrounds",
  "Sidebar",
  "Export",
  "Defaults",
] as const;
export type FeatureArea = (typeof FEATURE_AREAS)[number];

/** Every role: a control that had no role check before its feature. */
const EVERY_ROLE: readonly UserRole[] = ALL_ROLES;
const SUPERADMIN_ONLY: readonly UserRole[] = ["superadmin"];
/** The roles with the capability: a control that had the check `can(role, capability)`. */
const rolesThatCan = (capability: Capability): readonly UserRole[] =>
  ALL_ROLES.filter((role) => can(role, capability));

/**
 * A control that changes a state keeps showing to a role without the feature
 * while the state is not the default (the eye of an excluded banner, the chain
 * of a detached one): the role must be able to undo what a saved scene or a
 * default scene gives it. The description says so where it applies.
 */
export const FEATURES = [
  // --- Banner header -------------------------------------------------------
  {
    key: "banner.export-eye",
    name: "Eye: include a banner in the export",
    description:
      "The eye in a banner header (Shift+click: only this banner). Without the feature every " +
      "banner is exported; a banner that a scene excludes keeps its eye, so it can come back.",
    area: "Banner header",
    defaultRoles: EVERY_ROLE,
  },
  {
    key: "banner.text-chain",
    name: "Chain: detach a banner from the shared text",
    description:
      "The chain in a banner header (not on Releases, which has the scope tick). A detached " +
      "banner has its own text, image and look. Without the feature the banners stay synced; " +
      "a detached banner keeps its chain, so it can be synced again.",
    area: "Banner header",
    defaultRoles: EVERY_ROLE,
  },
  {
    key: "banner.own-bg-upload",
    name: "Own background of one banner",
    description:
      "The upload button of a detached banner: an image for this banner only. The remove " +
      "button stays while the banner has its own image.",
    area: "Banner header",
    defaultRoles: EVERY_ROLE,
  },
  // --- Canvas shortcuts ----------------------------------------------------
  {
    key: "banner.logo-cycle",
    name: "Alt+click on a logo: next logo",
    description:
      "Alt+click on a banner logo shows the next logo variant; Alt+Shift+click also freezes " +
      "the text colour.",
    area: "Canvas shortcuts",
    defaultRoles: EVERY_ROLE,
  },
  {
    key: "banner.text-recolor",
    name: "Alt+click on text: text colour",
    description: "Alt+click on banner text cycles its colour: default, light, black.",
    area: "Canvas shortcuts",
    defaultRoles: EVERY_ROLE,
  },
  {
    key: "banner.button-recolor",
    name: "Option+click on a button: button colour",
    description: "Option+click on the call-to-action pill of an email banner: light or dark.",
    area: "Canvas shortcuts",
    defaultRoles: EVERY_ROLE,
  },
  // --- Backgrounds ---------------------------------------------------------
  {
    key: "bg.banner-tools",
    name: "Background tools on a banner",
    description:
      "The pencil on a banner background: move, zoom, rotate, flip, fit, reset. Without the " +
      "feature each banner keeps the position that the default or the scene gives it.",
    area: "Backgrounds",
    defaultRoles: EVERY_ROLE,
  },
  {
    key: "bg.figma-cut-tools",
    name: "Position Figma cuts",
    description:
      "The background tools (move, zoom, rotate, flip) also on a ready cut: a banner image " +
      "that FigBaker cut from Figma. Without the feature a ready cut has no tools, because " +
      "it is exact by construction. A moved cut is a positioned image: it gets the tools for every role.",
    area: "Backgrounds",
    defaultRoles: SUPERADMIN_ONLY,
  },
  {
    key: "bg.upload",
    name: "Upload a background",
    description:
      "The Upload File tile of the background panel. The tile stays while the scene shows an upload.",
    area: "Backgrounds",
    defaultRoles: EVERY_ROLE,
  },
  {
    key: "bg.generative",
    name: "Generative background tile",
    description:
      "The generative gradient tile, where the page offers it. The tile stays while it is the background.",
    area: "Backgrounds",
    defaultRoles: EVERY_ROLE,
  },
  {
    key: "bg.generative-animation",
    name: "Generative animation controls",
    description: "Pause, Regenerate and the Duration, Elements and Waypoints sliders of the generative background.",
    area: "Backgrounds",
    defaultRoles: EVERY_ROLE,
  },
  {
    key: "bg.base-color",
    name: "Base colour",
    description: "The base colour picker and hex field. Without it the base colour of the default stays.",
    area: "Backgrounds",
    defaultRoles: EVERY_ROLE,
  },
  {
    key: "bg.look-sliders",
    name: "Brightness, blur and opacity sliders",
    description: "The background sliders of the panel (Releases: brightness and opacity).",
    area: "Backgrounds",
    defaultRoles: EVERY_ROLE,
  },
  {
    key: "bg.gradient-stops",
    name: "Gradient stops",
    description: "The stop editor of a gradient background.",
    area: "Backgrounds",
    defaultRoles: EVERY_ROLE,
  },
  {
    key: "bg.pixelation",
    name: "Pixelation",
    description:
      "The Pixelation panel of Releases (mosaic, dots, brush). The panel stays while the " +
      "scene has the mosaic on, so it can be turned off.",
    area: "Backgrounds",
    defaultRoles: EVERY_ROLE,
  },
  {
    key: "bg.list-editor",
    name: "Edit the background list",
    description:
      "The pencil of the background panel: reorder, hide and publish tiles. Needs the bg list permission.",
    area: "Backgrounds",
    defaultRoles: rolesThatCan("editBgList"),
    requires: "editBgList",
  },
  {
    key: "bg.set-order",
    name: "Order the backgrounds of a set",
    description:
      "The pencil of the background panel on a Releases set page, for a role without the list " +
      "editor: drag the tiles to give the set its own order, for everyone. Needs the set order permission.",
    area: "Backgrounds",
    defaultRoles: rolesThatCan("editSetBgOrder"),
    requires: "editSetBgOrder",
  },
  {
    key: "bg.promote-upload",
    name: "Add an upload to the backgrounds",
    description:
      "The + on an upload tile and on the link tile: the image becomes the first background. " +
      "Needs the default scene permission.",
    area: "Backgrounds",
    defaultRoles: rolesThatCan("editDefaultScenes"),
    requires: "editDefaultScenes",
  },
  // --- Sidebar -------------------------------------------------------------
  {
    key: "sidebar.typograf",
    name: "Typograf",
    description:
      "The Typograf toggle (not on Releases). The toggle stays while Typograf is on, so it can be turned off.",
    area: "Sidebar",
    defaultRoles: EVERY_ROLE,
  },
  {
    key: "sidebar.logo-toggle",
    name: "Logo on/off",
    description:
      "The Logo toggle of Social. The toggle stays while the logo is off, so it can be turned on again.",
    area: "Sidebar",
    defaultRoles: EVERY_ROLE,
  },
  {
    key: "sidebar.localization",
    name: "Localization",
    description:
      "The Localization toggle and font of Social and Email. The toggle stays while localization is on.",
    area: "Sidebar",
    defaultRoles: EVERY_ROLE,
  },
  {
    key: "sidebar.copy-link",
    name: "Copy link",
    description: "The button that saves the scene and copies its link.",
    area: "Sidebar",
    defaultRoles: EVERY_ROLE,
  },
  // --- Export --------------------------------------------------------------
  {
    key: "export.animated",
    name: "Video and GIF export",
    description:
      "The video button in the header of an animated banner: duration, Get video, Get GIF. " +
      "Export still gives a still image.",
    area: "Export",
    defaultRoles: EVERY_ROLE,
  },
  {
    key: "export.video-quality",
    name: "Video resolution and format",
    description: "720p / 1080p / 2160p and MP4 / WebM of the server video export. Without it: 1080p MP4.",
    area: "Export",
    defaultRoles: EVERY_ROLE,
  },
  // --- Defaults ------------------------------------------------------------
  {
    key: "defaults.panel",
    name: "Defaults panel",
    description: "Save as default and the other default controls. Needs the default scene permission.",
    area: "Defaults",
    defaultRoles: rolesThatCan("editDefaultScenes"),
    requires: "editDefaultScenes",
  },
  {
    key: "defaults.replicate-bg",
    name: "Copy a background position to other products",
    description: "The Copy to other products section and the batch upload of the Defaults panel.",
    area: "Defaults",
    defaultRoles: rolesThatCan("editDefaultScenes"),
    requires: "editDefaultScenes",
  },
  {
    key: "defaults.stage-order",
    name: "Stage order",
    description: "The stage order of Releases in the Defaults panel. Needs the stage order permission.",
    area: "Defaults",
    defaultRoles: rolesThatCan("editStageOrder"),
    requires: "editStageOrder",
  },
  {
    key: "defaults.selector-visibility",
    name: "Hide products, sets and layouts",
    description:
      "The products, sets and layouts of the Shown in selectors section of the Defaults panel. " +
      "Needs the selector visibility permission.",
    area: "Defaults",
    defaultRoles: rolesThatCan("editSelectorVisibility"),
    requires: "editSelectorVisibility",
  },
  {
    key: "defaults.background-visibility",
    name: "Backgrounds per stage and set",
    description:
      "The background tiles of the Shown in selectors section of the Defaults panel: hide a " +
      "background on this stage, in every set or in one set. Needs the background visibility permission.",
    area: "Defaults",
    defaultRoles: rolesThatCan("editBackgroundVisibility"),
    requires: "editBackgroundVisibility",
  },
] as const satisfies readonly FeatureDef[];

export type FeatureKey = (typeof FEATURES)[number]["key"];

const BY_KEY: ReadonlyMap<string, FeatureDef> = new Map(FEATURES.map((f) => [f.key, f]));

export function featureDef(key: string): FeatureDef | undefined {
  return BY_KEY.get(key);
}

export function isRegisteredFeature(key: string): key is FeatureKey {
  return BY_KEY.has(key);
}

/** Can the role have the feature at all? False when the role lacks `requires`. */
export function featureAllowedFor(def: FeatureDef, role: string | null | undefined): boolean {
  if (!ALL_ROLES.includes(role as UserRole)) return false;
  return def.requires ? can(role, def.requires) : true;
}

/** The roles that can have the feature, in ALL_ROLES order. */
export function featureAllowedRoles(def: FeatureDef): UserRole[] {
  return ALL_ROLES.filter((role) => featureAllowedFor(def, role));
}

/** The admin targets of one feature, as GET /api/flags returns them. */
export interface FeatureTargetsDoc {
  key: string;
  targets?: Partial<Record<string, boolean>> | null;
}

/**
 * Has the role the feature? The admin target for the role, else the default.
 * A role that cannot have the feature never has it, whatever the target says.
 */
export function resolveFeature(
  def: FeatureDef,
  role: string | null | undefined,
  doc: FeatureTargetsDoc | undefined
): boolean {
  if (!featureAllowedFor(def, role)) return false;
  const target = doc?.targets?.[role as UserRole];
  if (typeof target === "boolean") return target;
  return def.defaultRoles.includes(role as UserRole);
}

/** Every registered feature for the role. `docs` is the GET /api/flags list; undefined = none loaded. */
export function resolveFeatures(
  role: string | null | undefined,
  docs: readonly FeatureTargetsDoc[] | undefined
): Record<FeatureKey, boolean> {
  const byKey = new Map((docs ?? []).map((doc) => [doc.key, doc]));
  return Object.fromEntries(
    FEATURES.map((def) => [def.key, resolveFeature(def, role, byKey.get(def.key))])
  ) as Record<FeatureKey, boolean>;
}
